import { useState, useEffect, useRef } from '@wordpress/element';

/* =========================================================
   TSR Tracker — основной React-компонент
   ========================================================= */

const STORAGE_KEY = 'tsr_saved_items';
const REMOVE_ANIMATION_MS = 2000;

/* Форматирование даты без сдвига из-за UTC */
const formatDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '—';
    }

    return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

/* Преобразование YYYY-MM-DD в локальную дату */
const parseLocalDate = (value) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return null;
    }

    return date;
};

/* Получение сегодняшней даты в локальном часовом поясе */
const getTodayLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/* Склонение числительных */
const pluralize = (number, forms) => {
    const n = Math.abs(number) % 100;
    const last = n % 10;

    if (n >= 11 && n <= 19) return forms[2];
    if (last >= 2 && last <= 4) return forms[1];
    if (last === 1) return forms[0];

    return forms[2];
};

/* Форматирование срока в месяцах */
const formatTermString = (totalMonths) => {
    const months = Number(totalMonths);

    if (!Number.isFinite(months) || months <= 0) {
        return '';
    }

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const result = [];

    if (years > 0) {
        result.push(
            `${years} ${pluralize(years, ['год', 'года', 'лет'])}`
        );
    }

    if (remainingMonths > 0) {
        result.push(
            `${remainingMonths} ${pluralize(
                remainingMonths,
                ['месяц', 'месяца', 'месяцев']
            )}`
        );
    }

    return result.join(' ');
};

/* Определение срока ТСР.
   Правила:
   - бессрочные изделия остаются бессрочными;
   - сроки в днях и неопределённые сроки для выдаваемых партиями
     расходных изделий планируются как 3 месяца (со звёздочкой);
   - сроки в месяцах/годах из каталога сохраняются;
   - часы не используются для расчёта. */
const getEffectiveTerm = (item) => {
    const text = String(item.term_text || '').trim().toLowerCase();
    const numericMonths = Number(item.term_months);

    // Бессрочные изделия (например, собака-проводник) не переводим на 3 месяца.
    if (text.includes('бессрочно')) {
        return {
            type: 'unlimited',
            value: 0,
            label: 'Бессрочно',
            isConsumable: false,
            error: null,
        };
    }

    // В этом графике сроки в часах не рассчитываются.
    if (/\\b(час|часа|часов|часы|минут|минута|минуты|минуту)\\b/i.test(text)) {
        return {
            type: 'error',
            value: 0,
            label: 'Срок в часах не поддерживается',
            isConsumable: false,
            error: 'Срок указан в часах. Для расчёта графика используйте срок в днях, месяцах или годах.',
        };
    }

    // Короткие сроки выдачи (например, 3 дня или 15 дней) отображаем
    // как плановый период одной выдачи до 3 месяцев.
    const daysMatch = text.match(/(\\d+)\\s*(?:день|дня|дней|сутки|суток)/i);
    if (daysMatch) {
        return {
            type: 'months',
            value: 3,
            label: '3 месяца*',
            isConsumable: true,
            error: null,
        };
    }

    // Если срок в каталоге уже задан числом месяцев — сохраняем его.
    if (
        Number.isFinite(numericMonths) &&
        numericMonths > 0 &&
        text !== ''
    ) {
        return {
            type: 'months',
            value: numericMonths,
            label: formatTermString(numericMonths),
            isConsumable: false,
            error: null,
        };
    }

    // Разбираем срок в месяцах из текста, если числового значения нет.
    const monthsMatch = text.match(/(\\d+)\\s*(?:месяц|месяца|месяцев)/i);
    if (monthsMatch) {
        const months = Number(monthsMatch[1]);
        if (months > 0) {
            return {
                type: 'months',
                value: months,
                label: formatTermString(months),
                isConsumable: false,
                error: null,
            };
        }
    }

    // Разбираем срок в годах из текста.
    const yearsMatch = text.match(/(\\d+)\\s*(?:год|года|лет)/i);
    if (yearsMatch) {
        const months = Number(yearsMatch[1]) * 12;
        if (months > 0) {
            return {
                type: 'months',
                value: months,
                label: formatTermString(months),
                isConsumable: false,
                error: null,
            };
        }
    }

    // Как и в старой версии: если срок не указан числом и изделие не бессрочное,
    // используем условный трёхмесячный период, а не показываем ошибку.
    return {
        type: 'months',
        value: 3,
        label: '3 месяца*',
        isConsumable: true,
        error: null,
    };
};

/* Добавление месяцев с учётом последнего дня месяца.
   Например, 31 января + 1 месяц = последний день февраля. */
const addMonthsSafe = (sourceDate, months) => {
    const date = new Date(sourceDate);
    const originalDay = date.getDate();

    date.setDate(1);
    date.setMonth(date.getMonth() + months);

    const lastDay = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0
    ).getDate();

    date.setDate(Math.min(originalDay, lastDay));

    return date;
};

/* Расчёт дат замены и заказа сертификата */
const calculateDates = (dateString, termInfo) => {
    const receivedDate = parseLocalDate(dateString);

    if (!receivedDate) {
        return {
            nextDate: '—',
            certDate: '—',
            error: 'Укажите корректную дату получения ТСР.',
        };
    }

    if (termInfo.type === 'error') {
        return {
            nextDate: '—',
            certDate: '—',
            error: termInfo.error,
        };
    }

    if (termInfo.type === 'unlimited') {
        return {
            nextDate: 'Бессрочно',
            certDate: '—',
            error: null,
        };
    }

    let nextDate;

    if (termInfo.type === 'days') {
        nextDate = new Date(receivedDate);
        nextDate.setDate(nextDate.getDate() + termInfo.value);
    } else {
        nextDate = addMonthsSafe(receivedDate, termInfo.value);
    }

    const certDate = addMonthsSafe(nextDate, -1);

    return {
        nextDate: formatDate(nextDate),
        certDate: formatDate(certDate),
        error: null,
    };
};

/* =========================================================
   Компонент
   ========================================================= */

export default function App() {
    const [catalog, setCatalog] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('search');
    const [savedItems, setSavedItems] = useState({});
    const [exitingCodes, setExitingCodes] = useState({});
    const [storageError, setStorageError] = useState('');
    const [showLegalBasis, setShowLegalBasis] = useState(false);

    const removalTimers = useRef({});

    /* Загрузка каталога и сохранённого списка */
    useEffect(() => {
        if (
            window.tsrConfig &&
            Array.isArray(window.tsrConfig.catalogData)
        ) {
            setCatalog(window.tsrConfig.catalogData);
        }

        try {
            const localMemory = window.localStorage.getItem(STORAGE_KEY);

            if (localMemory) {
                const parsed = JSON.parse(localMemory);

                if (
                    parsed &&
                    typeof parsed === 'object' &&
                    !Array.isArray(parsed)
                ) {
                    setSavedItems(parsed);
                }
            }
        } catch (error) {
            setStorageError(
                'Не удалось прочитать сохранённый список из браузера. Проверьте настройки хранения данных.'
            );
        }

        return () => {
            Object.values(removalTimers.current).forEach((timer) => {
                window.clearTimeout(timer);
            });
        };
    }, []);

    /* Сохранение списка */
    const updateSaved = (newSaved) => {
        setSavedItems(newSaved);

        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(newSaved)
            );
            setStorageError('');
        } catch (error) {
            setStorageError(
                'Не удалось сохранить изменения в браузере. Возможно, хранилище заполнено или заблокировано.'
            );
        }
    };

    /* Добавление или обычное снятие галочки в поиске */
    const toggleSearchItem = (item) => {
        if (savedItems[item.code]) {
            const newSaved = { ...savedItems };
            delete newSaved[item.code];
            updateSaved(newSaved);
            return;
        }

        const newSaved = {
            ...savedItems,
            [item.code]: {
                ...item,
                dateReceived: getTodayLocal(),
            },
        };

        updateSaved(newSaved);
    };

    /* Снятие галочки непосредственно в «Мой список».
       Строка остаётся на экране во время анимации.
       Переключения вкладки нет. */
    const removeWithAnimation = (code) => {
        if (exitingCodes[code]) {
            return;
        }

        setExitingCodes((current) => ({
            ...current,
            [code]: true,
        }));

        if (removalTimers.current[code]) {
            window.clearTimeout(removalTimers.current[code]);
        }

        removalTimers.current[code] = window.setTimeout(() => {
            setSavedItems((current) => {
                const updated = { ...current };
                delete updated[code];

                try {
                    window.localStorage.setItem(
                        STORAGE_KEY,
                        JSON.stringify(updated)
                    );
                    setStorageError('');
                } catch (error) {
                    setStorageError(
                        'Не удалось сохранить изменения в браузере.'
                    );
                }

                return updated;
            });

            setExitingCodes((current) => {
                const updated = { ...current };
                delete updated[code];
                return updated;
            });

            delete removalTimers.current[code];
        }, REMOVE_ANIMATION_MS);
    };

    /* Изменение даты получения */
    const handleDateChange = (code, newDate) => {
        if (!parseLocalDate(newDate)) {
            setSavedItems((current) => ({
                ...current,
                [code]: {
                    ...current[code],
                    dateReceived: newDate,
                },
            }));
            return;
        }

        const newSaved = {
            ...savedItems,
            [code]: {
                ...savedItems[code],
                dateReceived: newDate,
            },
        };

        updateSaved(newSaved);
    };

    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredCatalog = catalog.filter((item) => {
        const title = String(item.title || '').toLowerCase();
        const code = String(item.code || '').toLowerCase();

        return (
            title.includes(normalizedSearch) ||
            code.includes(normalizedSearch)
        );
    });

    const savedItemsList = Object.values(savedItems);
    const hasConsumables = savedItemsList.some((item) =>
        getEffectiveTerm(item).isConsumable
    );

    return (
        <div className="tsr-app">
            <style>{`
                .tsr-app {
                    background: var(--tsr-bg, #fff);
                    border-radius: var(--tsr-radius, 12px);
                    padding: 20px;
                    font-family: inherit;
                    box-sizing: border-box;
                }

                .tsr-app *,
                .tsr-app *::before,
                .tsr-app *::after {
                    box-sizing: border-box;
                }

                .tsr-tabs {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                    margin-bottom: 20px;
                    border-bottom: 2px solid #eee;
                    padding-bottom: 10px;
                }

                .tsr-tab {
                    padding: 10px 20px;
                    cursor: pointer;
                    border: none;
                    border-radius: 6px;
                    font-size: 15px;
                    transition: background .2s ease, color .2s ease;
                }

                .tsr-tab.active {
                    background: #007cba;
                    color: #fff;
                }

                .tsr-tab:not(.active) {
                    background: #f0f0f0;
                    color: #333;
                }

                .tsr-legal-panel {
                    margin: 0 0 18px;
                    padding: 14px 16px;
                    border: 1px solid #d9e2ec;
                    border-left: 4px solid #007cba;
                    border-radius: 6px;
                    background: #f7fafc;
                    color: #263238;
                    line-height: 1.5;
                    font-size: 14px;
                }

                .tsr-legal-panel p {
                    margin: 6px 0 0;
                }

                .tsr-search-input {
                    width: 100%;
                    padding: 12px;
                    margin-bottom: 20px;
                    border-radius: 6px;
                    border: 1px solid #ccc;
                    font: inherit;
                }

                .tsr-table-wrap {
                    max-height: 600px;
                    overflow: auto;
                    border: 1px solid #eee;
                    border-radius: 6px;
                }

                .tsr-table {
                    width: 100%;
                    border-collapse: collapse;
                    text-align: left;
                    font-size: 14px;
                    margin: 0;
                }

                .tsr-table th,
                .tsr-table td {
                    padding: 12px;
                    border-bottom: 1px solid #eee;
                    vertical-align: middle;
                }

                .tsr-table thead {
                    background: #f8f9fa;
                }

                .tsr-checkbox {
                    width: 19px;
                    height: 19px;
                    cursor: pointer;
                    accent-color: #007cba;
                    vertical-align: middle;
                }

                .tsr-selected-row {
                    background: #f0f8ff;
                }

                .tsr-saved-overflow {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }

                .tsr-saved-table {
                    width: 100%;
                    min-width: 760px;
                    table-layout: fixed;
                }

                /* На экране название ТСР получает максимум места, даты — компактные. */
                .tsr-saved-table th:nth-child(1),
                .tsr-saved-table td:nth-child(1) { width: 55px; }
                .tsr-saved-table th:nth-child(2),
                .tsr-saved-table td:nth-child(2) { width: auto; }
                .tsr-saved-table th:nth-child(3),
                .tsr-saved-table td:nth-child(3) { width: 155px; }
                .tsr-saved-table th:nth-child(4),
                .tsr-saved-table td:nth-child(4) { width: 175px; }
                .tsr-saved-table th:nth-child(5),
                .tsr-saved-table td:nth-child(5) { width: 150px; }

                .tsr-saved-table th {
                    line-height: 1.3;
                    overflow-wrap: normal;
                    word-break: normal;
                }

                .tsr-saved-table td:nth-child(2) {
                    overflow-wrap: anywhere;
                    word-break: normal;
                }

                .tsr-saved-table input[type="date"] {
                    display: block;
                    width: 100%;
                    max-width: 100%;
                    min-width: 0;
                    font: inherit;
                }

                @media (max-width: 640px) {
                    .tsr-app { padding: 12px; }
                    .tsr-tabs { gap: 6px; margin-bottom: 14px; }
                    .tsr-tab { padding: 9px 12px; font-size: 14px; }
                    .tsr-saved-overflow { margin-right: -2px; }
                    .tsr-saved-table { min-width: 700px; font-size: 13px; }
                    .tsr-saved-table th,
                    .tsr-saved-table td { padding: 8px; }
                    .tsr-saved-table th:nth-child(3),
                    .tsr-saved-table td:nth-child(3) { width: 140px; }
                    .tsr-saved-table th:nth-child(4),
                    .tsr-saved-table td:nth-child(4) { width: 160px; }
                    .tsr-saved-table th:nth-child(5),
                    .tsr-saved-table td:nth-child(5) { width: 135px; }
                }

                /* Только телефоны: строка каталога становится компактной карточкой.
                   На планшетах и компьютерах остаётся обычная таблица. */
                @media screen and (max-width: 480px) {
                    .tsr-table-wrap {
                        max-height: 600px;
                        overflow-x: hidden;
                        overflow-y: auto;
                    }

                    .tsr-table-wrap > .tsr-table {
                        display: block;
                        width: 100%;
                        min-width: 0;
                        table-layout: auto;
                    }

                    .tsr-table-wrap > .tsr-table thead {
                        display: none;
                    }

                    .tsr-table-wrap > .tsr-table tbody {
                        display: block;
                        width: 100%;
                    }

                    .tsr-table-wrap > .tsr-table tbody tr {
                        display: grid;
                        grid-template-columns: 48px minmax(76px, 0.9fr) minmax(0, 1.4fr);
                        width: 100%;
                        box-sizing: border-box;
                        border-bottom: 1px solid #dfe3e8;
                        background: #fff;
                    }

                    .tsr-table-wrap > .tsr-table tbody tr:nth-child(even) {
                        background: #f7f8fa;
                    }

                    .tsr-table-wrap > .tsr-table tbody td {
                        display: block;
                        min-width: 0;
                        box-sizing: border-box;
                        padding: 10px 8px;
                        border: 0;
                        overflow-wrap: anywhere;
                        white-space: normal;
                        align-self: stretch;
                    }

                    /* Верх карточки: выбор, код и срок. */
                    .tsr-table-wrap > .tsr-table tbody td:nth-child(1) {
                        grid-column: 1;
                        grid-row: 1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-right: 1px solid #e5e7eb;
                    }

                    .tsr-table-wrap > .tsr-table tbody td:nth-child(2) {
                        grid-column: 2;
                        grid-row: 1;
                        display: flex;
                        align-items: center;
                        font-weight: 600;
                        white-space: nowrap;
                        border-right: 1px solid #e5e7eb;
                    }

                    .tsr-table-wrap > .tsr-table tbody td:nth-child(4) {
                        grid-column: 3;
                        grid-row: 1;
                        display: flex;
                        align-items: center;
                        font-size: 12px;
                        line-height: 1.35;
                    }

                    /* Наименование занимает отдельную строку на всю ширину. */
                    .tsr-table-wrap > .tsr-table tbody td:nth-child(3) {
                        grid-column: 1 / -1;
                        grid-row: 2;
                        padding: 9px 10px 11px;
                        border-top: 1px solid #e5e7eb;
                        line-height: 1.4;
                        color: #374151;
                    }

                    .tsr-table-wrap > .tsr-table tbody td:nth-child(3)::before {
                        content: 'Наименование';
                        display: block;
                        margin-bottom: 3px;
                        color: #6b7280;
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: .03em;
                    }

                    .tsr-table-wrap > .tsr-table .tsr-checkbox {
                        width: 18px;
                        height: 18px;
                        flex: 0 0 auto;
                    }
                }

                .tsr-saved-table th,
                .tsr-saved-table td {
                    border: 1px solid #ddd;
                }

                .tsr-remove-row {
                    animation: tsr-collapse ${REMOVE_ANIMATION_MS}ms
                        cubic-bezier(.4, 0, .2, 1) forwards;
                    overflow: hidden;
                    pointer-events: none;
                }

                .tsr-remove-row td {
                    animation: tsr-cell-collapse ${REMOVE_ANIMATION_MS}ms
                        cubic-bezier(.4, 0, .2, 1) forwards;
                }

                @keyframes tsr-collapse {
                    0% {
                        opacity: 1;
                        transform: scaleY(1);
                    }
                    20% {
                        opacity: .7;
                    }
                    100% {
                        opacity: 0;
                        transform: scaleY(0);
                    }
                }

                @keyframes tsr-cell-collapse {
                    0% {
                        padding-top: 12px;
                        padding-bottom: 12px;
                        border-width: 1px;
                    }
                    100% {
                        padding-top: 0;
                        padding-bottom: 0;
                        border-width: 0;
                    }
                }

                .tsr-print-button {
                    padding: 11px 20px;
                    background: #16834a;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 15px;
                    transition: background .2s ease;
                }

                .tsr-print-button:hover {
                    background: #116b3c;
                }

                .tsr-warning {
                    margin: 12px 0;
                    padding: 12px;
                    color: #8a4b00;
                    background: #fff3cd;
                    border: 1px solid #ffe08a;
                    border-radius: 6px;
                    font-size: 13px;
                }

                .tsr-error {
                    margin: 12px 0;
                    padding: 12px;
                    color: #842029;
                    background: #f8d7da;
                    border: 1px solid #f5c2c7;
                    border-radius: 6px;
                    font-size: 13px;
                }

                .tsr-empty {
                    padding: 30px;
                    text-align: center;
                    background: #f9f9f9;
                    border-radius: 6px;
                    border: 1px dashed #ccc;
                }

                @media (prefers-reduced-motion: reduce) {
                    .tsr-remove-row,
                    .tsr-remove-row td {
                        animation-duration: 1ms !important;
                    }
                }

                .tsr-print-brand {
                    display: none;
                }

                @page {
                    /* Ориентацию выбирают в диалоге печати: портрет или альбом */
                    size: auto;
                    margin: 7mm;
                }

                @media print {
                    html,
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                    }

                    .tsr-print-brand {
                        display: flex !important;
                        align-items: center;
                        gap: 10px;
                        width: 100%;
                        padding: 0 0 8px;
                        margin: 0 0 12px;
                        border-bottom: 2px solid #007cba;
                        color: #222;
                        font-family: Arial, sans-serif;
                    }

                    .tsr-print-logo-link {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex: 0 0 auto;
                        text-decoration: none !important;
                    }

                    .tsr-print-logo {
                        display: block;
                        width: auto;
                        max-width: 112px;
                        max-height: 48px;
                        object-fit: contain;
                    }

                    .tsr-print-brand-text {
                        display: flex;
                        flex: 1 1 auto;
                        min-width: 0;
                        flex-direction: column;
                        gap: 2px;
                        font-size: 10pt;
                    }

                    .tsr-print-brand-text span {
                        color: #555;
                        font-size: 8pt;
                    }

                    .tsr-print-site {
                        flex: 0 0 auto;
                        margin-left: auto;
                        color: #555;
                        font-size: 8pt;
                    }

                    #tsr-print-area h2 {
                        font-size: 15pt !important;
                        margin: 0 0 3px !important;
                        line-height: 1.2 !important;
                    }

                    #tsr-print-area > p {
                        margin: 0 0 10px !important;
                        font-size: 8.5pt !important;
                    }
                    body * {
                        visibility: hidden !important;
                    }

                    #tsr-print-area,
                    #tsr-print-area * {
                        visibility: visible !important;
                    }

                    #tsr-print-area {
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        overflow: visible !important;
                    }

                    .tsr-no-print,
                    .tsr-print-button,
                    .tsr-checkbox,
                    .tsr-error {
                        display: none !important;
                    }

                    .tsr-table-wrap,
                    .tsr-saved-overflow {
                        max-height: none !important;
                        overflow: visible !important;
                        border: none !important;
                    }

                    .tsr-saved-table {
                        table-layout: fixed !important;
                        min-width: 0 !important;
                        width: 100% !important;
                        font-size: 8pt !important;
                        line-height: 1.2 !important;
                        border-collapse: collapse !important;
                        border-spacing: 0 !important;
                    }

                    .tsr-saved-table thead {
                        display: table-header-group;
                    }

                    .tsr-saved-table tr {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .tsr-saved-table th,
                    .tsr-saved-table td {
                        padding: 4px 5px !important;
                        border: 1px solid #aeb8c2 !important;
                        color: #111 !important;
                        vertical-align: middle !important;
                        overflow-wrap: anywhere;
                    }

                    /* В печати убираем колонку выбора. Все ширины заданы одинаково для шапки и строк. */
                    .tsr-saved-table th:first-child,
                    .tsr-saved-table td:first-child { display: none !important; }
                    .tsr-saved-table th:nth-child(2),
                    .tsr-saved-table td:nth-child(2) { width: 48% !important; }
                    .tsr-saved-table th:nth-child(3),
                    .tsr-saved-table td:nth-child(3) { width: 16% !important; }
                    .tsr-saved-table th:nth-child(4),
                    .tsr-saved-table td:nth-child(4) { width: 19% !important; }
                    .tsr-saved-table th:nth-child(5),
                    .tsr-saved-table td:nth-child(5) { width: 17% !important; }

                    .tsr-saved-table th {
                        text-align: left !important;
                        white-space: normal !important;
                        word-break: normal !important;
                        overflow-wrap: break-word !important;
                        vertical-align: bottom !important;
                    }
                    .tsr-saved-table td:nth-child(2) {
                        overflow-wrap: break-word !important;
                        word-break: normal !important;
                    }

                    /* Чёткое разделение строк даже при печати без цветного фона. */
                    .tsr-saved-table tbody tr:nth-child(even) td {
                        background-color: #f0f3f6 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .tsr-saved-table tbody tr:nth-child(odd) td {
                        background-color: #fff !important;
                    }

                    /* По умолчанию — компактная таблица; название ТСР занимает максимум места. */
                    .tsr-saved-table th:nth-child(2),
                    .tsr-saved-table td:nth-child(2) { width: 44% !important; }
                    .tsr-saved-table th:nth-child(3),
                    .tsr-saved-table td:nth-child(3) { width: 18% !important; }
                    .tsr-saved-table th:nth-child(4),
                    .tsr-saved-table td:nth-child(4) { width: 19% !important; }
                    .tsr-saved-table th:nth-child(5),
                    .tsr-saved-table td:nth-child(5) { width: 19% !important; }

                    /* Вертикальный A4: уменьшаем шрифт и отступы, не меняя данные. */
                    @media print and (orientation: portrait) {
                        .tsr-print-brand { gap: 6px !important; padding-bottom: 4px !important; margin-bottom: 6px !important; }
                        .tsr-print-logo { max-width: 78px !important; max-height: 32px !important; }
                        .tsr-print-brand-text { font-size: 8pt !important; }
                        .tsr-print-brand-text span, .tsr-print-site { font-size: 6.5pt !important; }
                        #tsr-print-area h2 { font-size: 11pt !important; }
                        #tsr-print-area > p { font-size: 7pt !important; margin-bottom: 5px !important; }
                        .tsr-saved-table { font-size: 6.2pt !important; line-height: 1.12 !important; }
                        .tsr-saved-table th, .tsr-saved-table td { padding: 2px 3px !important; }
                        .tsr-saved-table th:nth-child(2), .tsr-saved-table td:nth-child(2) { width: 40% !important; }
                        .tsr-saved-table th:nth-child(3), .tsr-saved-table td:nth-child(3) { width: 18% !important; }
                        .tsr-saved-table th:nth-child(4), .tsr-saved-table td:nth-child(4) { width: 21% !important; }
                        .tsr-saved-table th:nth-child(5), .tsr-saved-table td:nth-child(5) { width: 21% !important; }
                        .tsr-saved-table th small { font-size: 5.5pt !important; }
                        .tsr-print-legend { font-size: 6pt !important; gap: 3px 8px !important; margin-top: 5px !important; }
                        .tsr-warning { font-size: 5.8pt !important; line-height: 1.15 !important; padding: 4px 5px !important; margin-top: 5px !important; }
                    }

                    .tsr-print-date { display: inline !important; font-weight: 700; color: #0759a6 !important; }
                    .tsr-saved-table td.tsr-date-cell { color: #0759a6 !important; background: #eaf3ff !important; font-weight: 700 !important; }
                    .tsr-saved-table td.tsr-cert-cell { color: #a61b1b !important; background: #fff0f0 !important; font-weight: 700 !important; }
                    .tsr-saved-table td.tsr-next-cell { color: #116b3c !important; background: #eaf8ef !important; font-weight: 700 !important; }
                    .tsr-print-legend { display: flex !important; flex-wrap: wrap; gap: 6px 16px; margin: 9px 0 0; font-size: 8pt; }
                    .tsr-print-legend span { display: inline-flex; align-items: center; gap: 5px; }
                    .tsr-legend-dot { display: inline-block; width: 9px; height: 9px; border-radius: 2px; }
                    .tsr-legend-blue { background: #1671c5; }
                    .tsr-legend-red { background: #c62828; }
                    .tsr-legend-green { background: #16834a; }

                    .tsr-saved-table td:nth-child(3) input[type="date"] { display: none !important; }

                    .tsr-saved-table th small {
                        font-size: 7pt !important;
                    }

                    .tsr-remove-row {
                        display: none !important;
                    }

                    .tsr-warning {
                        margin-top: 8px !important;
                        padding: 7px 9px !important;
                        background: #fff !important;
                        color: #222 !important;
                        border: 1px solid #b7c1ca !important;
                        font-size: 7.5pt !important;
                        line-height: 1.3 !important;
                    }

                    input[type="date"] { display: none !important; }
                }
            `}</style>

            {/* Навигация */}
            <div className="tsr-tabs tsr-no-print">
                <button
                    type="button"
                    className={`tsr-tab ${activeTab === 'search' ? 'active' : ''}`}
                    onClick={() => setActiveTab('search')}
                >
                    Поиск ТСР
                </button>

                <button
                    type="button"
                    className={`tsr-tab ${activeTab === 'saved' ? 'active' : ''}`}
                    onClick={() => setActiveTab('saved')}
                >
                    Мой список ({savedItemsList.length})
                </button>

                <button
                    type="button"
                    className={`tsr-tab ${showLegalBasis ? 'active' : ''}`}
                    onClick={() => setShowLegalBasis((current) => !current)}
                    aria-expanded={showLegalBasis}
                >
                    Нормативная база
                </button>
            </div>

            {showLegalBasis && (
                <div className="tsr-legal-panel" role="region" aria-label="Нормативная база">
                    <strong>Приказ Минтруда России от 14.07.2025 № 438н</strong>
                    <p>
                        «Об утверждении сроков пользования техническими средствами
                        реабилитации, протезами и ортопедическими изделиями».
                    </p>
                    <p>
                        Приказ утверждает сроки пользования ТСР, используемые в графике
                        для ориентировочного расчёта следующего срока обеспечения.
                        Даты в сервисе являются плановыми и не заменяют разъяснения СФР.
                    </p>
                    <p>
                        Срок действия электронного сертификата и дата его начала —
                        отдельные сведения; проверяйте их в оформленном сертификате.
                    </p>
                </div>
            )}

            {storageError && (
                <div className="tsr-error" role="alert">
                    {storageError}
                </div>
            )}

            {/* Вкладка поиска */}
            {activeTab === 'search' && (
                <div>
                    <input
                        className="tsr-search-input"
                        type="search"
                        placeholder="Поиск по коду (например: 6-01-01) или названию..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        aria-label="Поиск ТСР"
                    />

                    <div className="tsr-table-wrap">
                        <table className="tsr-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>Выбор</th>
                                    <th style={{ width: 110 }}>Код</th>
                                    <th>Наименование</th>
                                    <th style={{ width: 190 }}>Период</th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredCatalog.length > 0 ? (
                                    filteredCatalog.map((item) => {
                                        const termInfo = getEffectiveTerm(item);
                                        const checked = Boolean(savedItems[item.code]);

                                        return (
                                            <tr
                                                key={item.code || item.id}
                                                className={checked ? 'tsr-selected-row' : ''}
                                            >
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        className="tsr-checkbox"
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleSearchItem(item)}
                                                        aria-label={`Выбрать ${item.title}`}
                                                    />
                                                </td>

                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {item.code}
                                                </td>

                                                <td>{item.title}</td>

                                                <td>
                                                    <span
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: termInfo.type === 'error'
                                                                ? '#b42318'
                                                                : '#333',
                                                        }}
                                                    >
                                                        {termInfo.label}
                                                    </span>

                                                    {termInfo.error && (
                                                        <div className="tsr-error">
                                                            {termInfo.error}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center' }}>
                                            Ничего не найдено
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Вкладка «Мой список» */}
            {activeTab === 'saved' && (
                <div id="tsr-print-area">
                    <div className="tsr-print-brand">
                        <a className="tsr-print-logo-link" href="https://nnd.name/" target="_blank" rel="noreferrer" aria-label="Открыть сайт NND — Нас Не Догонят">
                            <img className="tsr-print-logo" src="https://nnd.name/wp-content/uploads/2015/05/logoZ.png" alt="NND — Нас Не Догонят" />
                        </a>
                        <div className="tsr-print-brand-text">
                            <strong>График получения и замены ТСР</strong>
                            <span>Сервис планирования на сайте «Нас Не Догонят»</span>
                        </div>
                        <a className="tsr-print-site" href="https://nnd.name/" target="_blank" rel="noreferrer">nnd.name</a>
                    </div>
                    <h2 style={{ marginTop: 0, marginBottom: 5 }}>
                        График замены ваших ТСР
                    </h2>

                    <p
                        style={{
                            color: '#666',
                            fontSize: 14,
                            marginTop: 0,
                            marginBottom: 20,
                        }}
                    >
                        Укажите дату получения ТСР или оплаты предыдущего электронного сертификата (ЭС).
                    </p>

                    {savedItemsList.length > 0 ? (
                        <>
                            <div className="tsr-saved-overflow" style={{ overflowX: 'auto' }}>
                                <table className="tsr-table tsr-saved-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 55 }}>Выбор</th>
                                            <th>ТСР</th>
                                            <th style={{ width: 155 }}>
                                                Дата получения ТСР / оплаты ЭС
                                                <br />
                                                <small style={{ fontWeight: 'normal' }}>
                                                    ТСР — дата получения; ЭС — дата оплаты
                                                </small>
                                            </th>
                                            <th style={{ width: 175 }}>
                                                Заказать ЭС / оформить получение ТСР
                                                <br />
                                                <small style={{ fontWeight: 'normal' }}>
                                                    за 1 месяц до окончания срока
                                                </small>
                                            </th>
                                            <th>Новое ТСР или ЭС</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {savedItemsList.map((item) => {
                                            const termInfo = getEffectiveTerm(item);
                                            const dates = calculateDates(
                                                item.dateReceived,
                                                termInfo
                                            );
                                            const isExiting = Boolean(
                                                exitingCodes[item.code]
                                            );

                                            return (
                                                <tr
                                                    key={item.code}
                                                    className={isExiting ? 'tsr-remove-row' : ''}
                                                    aria-hidden={isExiting ? 'true' : undefined}
                                                >
                                                    <td
                                                        className="tsr-no-print"
                                                        style={{ textAlign: 'center' }}
                                                    >
                                                        <input
                                                            className="tsr-checkbox"
                                                            type="checkbox"
                                                            checked={!isExiting}
                                                            disabled={isExiting}
                                                            onChange={(event) => {
                                                                if (!event.target.checked) {
                                                                    removeWithAnimation(item.code);
                                                                }
                                                            }}
                                                            aria-label={`Оставить ${item.title} в моём списке`}
                                                        />
                                                    </td>

                                                    <td>
                                                        <strong>{item.code}</strong>
                                                        <br />
                                                        {item.title}
                                                        <br />
                                                        <small
                                                            style={{
                                                                color: termInfo.type === 'error'
                                                                    ? '#b42318'
                                                                    : '#007cba',
                                                            }}
                                                        >
                                                            Период: {termInfo.label}
                                                        </small>
                                                    </td>

                                                    <td className="tsr-date-cell">
                                                        <span className="tsr-print-date" style={{ display: 'none' }}>
                                                            {parseLocalDate(item.dateReceived) ? formatDate(parseLocalDate(item.dateReceived)) : 'Не указана'}
                                                        </span>
                                                        <input
                                                            type="date"
                                                            value={item.dateReceived || ''}
                                                            disabled={isExiting}
                                                            onChange={(event) =>
                                                                handleDateChange(
                                                                    item.code,
                                                                    event.target.value
                                                                )
                                                            }
                                                            aria-label={`Дата получения ТСР или оплаты электронного сертификата: ${item.title}`}
                                                            style={{
                                                                padding: 8,
                                                                border: '1px solid #ccc',
                                                                borderRadius: 4,
                                                                width: '100%',
                                                                minWidth: 0,
                                                            }}
                                                        />
                                                    </td>

                                                    <td className="tsr-cert-cell" style={{ fontWeight: 'bold', color: '#a61b1b' }}>
                                                        {dates.certDate}
                                                    </td>

                                                    <td className="tsr-next-cell" style={{ fontWeight: 'bold', color: '#16834a' }}>
                                                        {dates.nextDate}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {savedItemsList.some((item) => {
                                const termInfo = getEffectiveTerm(item);
                                const dates = calculateDates(item.dateReceived, termInfo);
                                return Boolean(dates.error);
                            }) && (
                                <div className="tsr-error">
                                    <strong>Обратите внимание:</strong> для некоторых
                                    изделий не удалось рассчитать даты. Проверьте дату
                                    получения и срок замены в каталоге.
                                </div>
                            )}

                            <div className="tsr-print-legend" style={{ display: 'none' }} aria-label="Расшифровка цветов">
                                <span><i className="tsr-legend-dot tsr-legend-blue" /> <strong>Синий</strong> — дата фактического получения ТСР</span>
                                <span><i className="tsr-legend-dot tsr-legend-red" /> <strong>Красный</strong> — когда заказать электронный сертификат или оформить получение</span>
                                <span><i className="tsr-legend-dot tsr-legend-green" /> <strong>Зелёный</strong> — плановая дата получения нового ТСР</span>
                            </div>

                            {hasConsumables && (
                                <div className="tsr-warning">
                                    <strong>* Примечание:</strong> для расходных изделий, у которых в каталоге
                                    указан срок в днях или срок не определён, в графике условно
                                    используется период 3 месяца — максимальный плановый период
                                    одной выдачи. При необходимости потребность можно учитывать
                                    чаще, например еженедельно или ежемесячно. Фактические объём
                                    и порядок выдачи зависят от условий обеспечения.
                                </div>
                            )}

                            <div className="tsr-no-print" style={{ marginTop: 16 }}>
                                <button
                                    type="button"
                                    className="tsr-print-button"
                                    onClick={() => window.print()}
                                >
                                    🖨 Печать / сохранить в PDF
                                </button>

                                <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                                    В окне печати выберите принтер или «Сохранить как PDF».
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="tsr-empty">
                            Ваш список пуст. Перейдите во вкладку «Поиск ТСР» и отметьте
                            нужные изделия галочками.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}